import { useCallback, useRef, useState } from "react";

/**
 * Tracks the inline-size of a DOM element via ResizeObserver.
 * Returns a callback ref (attach to the element) and the current width in px.
 *
 * Uses a callback ref so the observer is attached/detached automatically
 * when the element mounts/unmounts — no useEffect needed.
 */
export function useContainerWidth(): [callbackRef: (node: HTMLElement | null) => void, widthPx: number] {
    const [width, setWidth] = useState(0);
    const observerRef = useRef<ResizeObserver | null>(null);
    const nodeRef = useRef<HTMLElement | null>(null);

    const callbackRef = useCallback((node: HTMLElement | null) => {
        // Disconnect previous observer
        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
        }

        nodeRef.current = node;

        if (!node) {
            return;
        }

        // Read initial size synchronously so the first render has a value
        setWidth(node.getBoundingClientRect().width);

        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                // Prefer borderBoxSize (inline-size) when available
                const inlineSize = entry.borderBoxSize?.[0]?.inlineSize;
                if (inlineSize !== undefined) {
                    setWidth(inlineSize);
                } else {
                    setWidth(entry.contentRect.width);
                }
            }
        });

        observer.observe(node, { box: "border-box" });
        observerRef.current = observer;
    }, []);

    return [callbackRef, width];
}
